import { format, startOfMonth, subMonths } from "date-fns";
import { getGrowthEfficiencyCreatedDate } from "../state-of-sales-v2/growthEfficiencyData";
import { creationValueInCompanyCurrency, IT_SOFTWARE_NEW_ARR_PIPELINES } from "./creationExploration";
import type { ArrCreationPlan } from "@revelate/types/required-pipeline-creation";
import type { VerifiedCreationHistory } from "./creationPlanning";
import type { PipelineHealthData } from "./presentation";
import { CREATION_EXPLORATION_COPY as COPY } from "./copy";

/** Approved 2026-10-25: attribute today's saved ARR to the original creation month.
 * This is an explicit reporting convention, not a reconstructed value snapshot.
 * TODO(Emil/Stefan/Zabir): revisit the provisional business inputs, not deal history.
 */
export function currentValueCreationHistory(data: PipelineHealthData, plan: ArrCreationPlan | null): VerifiedCreationHistory | undefined {
  const context = data.context.data;
  if (context?.company.id !== 53 || data.context.isLoading || !data.currentSnapshot || !plan || !context.currencies) return undefined;
  const currency = context.currencies.find(row => row.id === context.company.default_currency_id)?.code;
  if (!currency || currency !== plan.money.currency) return undefined;
  const pipelines = ((context.pipelines as { results?: Array<{ id: string; label: string }> })?.results ?? []);
  const pipelineIds = new Set(pipelines.filter(row => IT_SOFTWARE_NEW_ARR_PIPELINES.includes(row.label)).map(row => String(row.id)));
  if (!pipelineIds.size) return undefined;
  const from = subMonths(startOfMonth(data.asOf), 24);
  const deals = context.rawDeals.flatMap(raw => {
    if (raw.provider !== "hubspot" || !pipelineIds.has(String(raw.pipeline))) return [];
    const deal = { ...raw, emails: undefined, meetings: undefined, activity_sync_status: undefined };
    const createdAt = getGrowthEfficiencyCreatedDate(deal);
    if (!createdAt || createdAt < from || createdAt >= data.asOf) return [];
    const value = raw.value === null || raw.value === undefined ? null : creationValueInCompanyCurrency(Number(raw.value), raw.currency_id, context);
    const isWon = raw.is_won === true;
    // Only the agreed stored outcome/date is used. No CRM expected-close fallback.
    const closedAt = raw.closed_at ? new Date(raw.closed_at) : null;
    return [{ id: String(raw.id), createdAt, closedAt, isWon, isLost: raw.is_lost === true,
      currency, createdValue: value, wonValue: isWon ? value : null }];
  });
  return {
    companyId: context.company.id, deals,
    // Explicit observation policy, not a fixed sales-cycle prediction. The existing
    // mature-cohort helper still requires >=30 eligible deals and >=5 observed wins.
    horizonDays: 365, minimumSeasonDealsPerMonth: 1,
    verification: { money: { currency, unit: "annualized-recurring-revenue" }, newSalesScope: true, activationTiming: true,
      values: "current-values-validated", valueLimitation: COPY.currentValueBasis,
      // The existing loader paginates ALL retained deals before publishing context.
      // Coverage is that completed query's 24-month retained-record window, not an
      // invented watermark derived from the oldest record or a CRM archive promise.
      completeFrom: format(from, "yyyy-MM") },
  };
}
